import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const lastUpdated = 'February 16, 2026';

  return (
    <div className="min-h-screen bg-gray-50 py-12" data-testid="privacy-policy-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center text-sm text-gray-600 hover:text-orange-600 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
        </Link>

        <div className="bg-white rounded-lg shadow-sm border p-8 md:p-12">
          <div className="flex items-center gap-3 mb-2">
            <Lock className="h-8 w-8 text-orange-600" />
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Privacy Policy</h1>
          </div>
          <p className="text-sm text-gray-500 mb-8">Last updated: {lastUpdated}</p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            <section>
              <p>
                <strong>Hi-Viz Pro Kenya Limited</strong> ("we", "us", "our") respects your privacy and is committed to
                protecting your personal data. This policy explains what information we collect, how we use it, and your
                rights under the Kenya Data Protection Act, 2019.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Information We Collect</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Account data:</strong> name, email, password (hashed), phone number.</li>
                <li><strong>Order data:</strong> shipping address, items purchased, order history.</li>
                <li><strong>Payment data:</strong> processed securely by Stripe — we never store full card numbers on our servers.</li>
                <li><strong>Technical data:</strong> IP address, browser type, pages visited (via standard web analytics).</li>
                <li><strong>Communication data:</strong> messages you send us via email, WhatsApp, or contact forms.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. How We Use Your Information</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>To process and fulfil your orders, including delivery and customer support.</li>
                <li>To send transactional emails (order confirmations, shipping updates, refunds).</li>
                <li>To improve our website, products, and services.</li>
                <li>To comply with legal obligations (tax records, anti-fraud, regulatory reporting).</li>
                <li>With your consent, to send marketing communications. You can opt out at any time.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Sharing With Third Parties</h2>
              <p>We share only the minimum necessary data with trusted third-party processors:</p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li><strong>Stripe</strong> — payment processing</li>
                <li><strong>Resend</strong> — transactional email delivery</li>
                <li><strong>Delivery partners</strong> — for shipping your orders</li>
                <li><strong>Cloud hosting providers</strong> — for storing website and order data securely</li>
              </ul>
              <p className="mt-3">We do not sell your personal information to advertisers or any third parties.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Data Security</h2>
              <p>
                We implement industry-standard security measures including encrypted connections (HTTPS), hashed passwords,
                role-based access control, and regular security audits. However, no system is 100% secure — please use a
                strong password and notify us immediately of any suspected breach.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Cookies</h2>
              <p>
                We use essential cookies to keep you logged in and remember your shopping cart. Analytics cookies help us
                understand site usage. You can disable cookies in your browser, but some features may not work correctly.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Data Retention</h2>
              <p>
                We retain your account and order data for as long as your account is active and for up to 7 years afterwards
                to comply with Kenyan tax and commercial law requirements.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Your Rights</h2>
              <p>Under the Kenya Data Protection Act, you have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>Access the personal data we hold about you.</li>
                <li>Request correction of inaccurate data.</li>
                <li>Request deletion of your data (subject to legal retention requirements).</li>
                <li>Withdraw consent for marketing communications.</li>
                <li>Lodge a complaint with the Office of the Data Protection Commissioner of Kenya.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Contact Us</h2>
              <p>For privacy questions or to exercise your rights, contact our Data Protection Officer:</p>
              <ul className="list-none pl-0 space-y-1 mt-3">
                <li><strong>Email:</strong> <a href="mailto:info@hivizprokenya.co.ke" className="text-orange-600 hover:underline">info@hivizprokenya.co.ke</a></li>
                <li><strong>Phone / WhatsApp:</strong> +254 716 504 759</li>
              </ul>
            </section>

            <div className="border-t pt-6 mt-8 text-sm text-gray-500">
              <p>
                See also: <Link to="/terms" className="text-orange-600 hover:underline">Terms & Conditions</Link>{' · '}
                <Link to="/refund-policy" className="text-orange-600 hover:underline">Refund Policy</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
